import { atatFetch } from '@ecomm/data-simplisafe-api/atatClient'
import {
  COOKIE_ATAT_TOKEN,
  COOKIE_VID,
  getAtAtToken,
  getVisitorId,
  setCookie
} from '@ecomm/shared-cookies'
import { Maybe } from '@simplisafe/ewok'
import { safeProp } from '@simplisafe/monda'
import * as O from 'fp-ts/lib/Option'
import { pipe } from 'fp-ts/lib/function'
import jwtDecode, { type JwtPayload } from 'jwt-decode'

import { logError } from '@ecomm/error-handling'
import type { TrackingData } from '@ecomm/tracking-types/Data'

export const handleAtAtTrackingEvent = (
  data: TrackingData,
  refreshCookieAtom: () => void
) => {
  // https://at-at.us-east-1.qa.commerce.simplisafe.com/api/doc
  const trackPageLoad = () => {
    const atatRequestBody = {
      optimizelyVisitor: {}, // TODO is this still needed?
      qq: data.pagePath?.replace(/^\/+|\/+$/g, ''), // strip leading and trailing slashes
      query: data.queryString,
      referer: data.referrer, // 'referer' in key is intentional
      title: data.pageTitle,
      uid: data.userId,
      vid: data.visitorId
    }

    const atatCookieOptions = {
      expires: new Date(new Date().setFullYear(new Date().getFullYear() + 1)), // store for 1 year
      path: '/',
      sameSite: <const>'strict',
      secure: process.env['NODE_ENV'] !== 'development' // disable secure for local development only
    }

    const handleFailure = (e: Error) => logError(e, { errorCategory: 'atat' })

    const handleSuccess = (response: Record<string, string>) =>
      pipe(
        response['token'],
        O.fromNullable,
        O.map(token => {
          !getAtAtToken() &&
            setCookie(COOKIE_ATAT_TOKEN, token, atatCookieOptions)
          const payload = jwtDecode<JwtPayload>(token)

          pipe(
            O.fromNullable(payload.sub),
            O.map(vid => {
              !getVisitorId() && setCookie(COOKIE_VID, vid, atatCookieOptions)
            })
          )

          // This refreshes the cookies in the jotai cookieAtom after document.cookie is set in the lines above.
          // TODO: longer-term this whole AT-AT flow should be refactored to use the `useCookie` hook directly.
          refreshCookieAtom()
        })
      )

    atatFetch(atatRequestBody, handleFailure, handleSuccess)?.catch(logError)
  }

  data.event === 'pageLoad' && trackPageLoad()
}

export const visitorIdAtAt: () => string | undefined = () =>
  Maybe.fromNull(getAtAtToken())
    .map(_token => jwtDecode<JwtPayload>(_token))
    .chain(safeProp('sub'))
    .orUndefined()

/**
 * Polls for the AT-AT cookie and returns a Promise that resolves with the cookie value.
 * NOTE: polling for cookies is not recommended and should be avoided if at all possible.
 */
export const fetchAtAtToken = (signal?: AbortSignal) =>
  new Promise<string | null>(resolve => {
    const clear = (interval: NodeJS.Timeout, value: string | null) => {
      clearInterval(interval)
      resolve(value)
    }

    const interval = setInterval(() => {
      const shouldCancel = !globalThis.window || signal?.aborted

      if (shouldCancel) {
        clear(interval, null)
      } else {
        const atatToken = getAtAtToken()
        !!atatToken && clear(interval, atatToken)
      }
    }, 100)
  })

/**
 * Polls for the AT-AT cookie and returns a Promise that resolves with the cookie's visitor id.
 * NOTE: polling for cookies is not recommended and should be avoided if at all possible.
 */
export const fetchAtAtVisitorId = (signal?: AbortSignal) =>
  fetchAtAtToken(signal).then(() => visitorIdAtAt())
